import React, { FC, useEffect, useState } from 'react';
import {
    DialogContent,
    Box,
    Grid,
    TextField,
    DialogActions,
    Button,
    FormControl,
    FormControlLabel,
    Switch,
    InputAdornment,
} from '@mui/material';
import { MaterialBatch, LabMaterial } from 'models/material';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { modifyMaterialTemplate } from 'apollo/templates/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import MaterialBatchManager from '../material-batch-manager.component.tsx/material-batch-manager.component';
import { getTime } from 'date-fns';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';

type EditMaterialFormProps = {
    onCloseDialog: () => void;
    materialToEdit: LabMaterial;
    onEditMaterial: (material: LabMaterial) => void;
};
const EditMaterialForm: FC<EditMaterialFormProps> = ({
    onCloseDialog,
    materialToEdit,
    onEditMaterial,
}: EditMaterialFormProps) => {
    const { t: translate } = useTranslation(['common', 'actions-materials', 'workflow', 'orders']);
    const [batches, setBatches] = useState<MaterialBatch[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();

    useEffect(() => {
        setBatches(materialToEdit.batches || []);
    }, []);

    const [modifyMaterial] = useMutation(modifyMaterialTemplate, {
        onCompleted: ({ modifyMaterial }) => {
            onEditMaterial(modifyMaterial);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (values: LabMaterial) => {
        const errors: Record<string, string> = {};
        if (!values.materialCode) {
            errors.materialCode = translate('actions-materials:VALIDATION:MATERIAL_CODE');
        }
        if (!values.description) {
            errors.description = translate('workflow:VALIDATION:DESCRIPTION');
        }
        return errors;
    };

    const batchesVars = (batches: MaterialBatch[]) => {
        const batchesVars: any = [];
        batches.forEach((batch) => {
            batchesVars.push({
                batchNumber: batch.batchNumber,
                quantity: batch.fullBatchQuantity, // Change to batch.quantity for next step of batch management
                fullBatchQuantity: batch.fullBatchQuantity,
                createdOn: typeof batch.createdOn === 'number' ? batch.createdOn : getTime(batch.createdOn),
                closed: batch.closed,
                closedDate:
                    typeof batch.closedDate === 'number'
                        ? batch.closedDate
                        : batch.closedDate
                          ? getTime(batch.closedDate)
                          : null,
            });
        });
        return batchesVars;
    };

    const formik = useFormik({
        initialValues: {
            ...materialToEdit,
            vat: materialToEdit.vat || 0,
            batchInventoryManagement: materialToEdit.batchInventoryManagement,
            inactive: materialToEdit.inactive,
        },
        validate,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const vat = parseFloat(values.vat?.toString() || '');
            await modifyMaterial({ variables: { ...values, vat: vat || 0, batches: batchesVars(batches) } });
            onCloseDialog();
        },
    });

    const handleBatches = (batches: MaterialBatch[]) => {
        setBatches(batches);
    };
    const handleUseInventory = (useInventory: boolean) => {
        formik.setFieldValue('batchInventoryManagement', useInventory);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <DialogContent>
                <Box mt={1} mb={4}>
                    <Grid container spacing={2}>
                        <Grid item md={4} sm={12}>
                            <FormControl fullWidth>
                                <TextField
                                    id="material-code"
                                    margin="dense"
                                    label={translate('actions-materials:CODE')}
                                    required
                                    helperText={formik.touched.materialCode && formik.errors.materialCode}
                                    error={formik.touched.materialCode && Boolean(formik.errors.materialCode)}
                                    {...formik.getFieldProps('materialCode')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item md={6} sm={12}>
                            <FormControl fullWidth>
                                <TextField
                                    id="description"
                                    margin="dense"
                                    label={translate('common:DESCRIPTION')}
                                    required
                                    helperText={formik.touched.description && formik.errors.description}
                                    error={formik.touched.description && Boolean(formik.errors.description)}
                                    {...formik.getFieldProps('description')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item md={2} sm={12}>
                            <FormControl fullWidth>
                                <NumberOnlyTextField
                                    id="vat"
                                    label={translate('orders:VAT')}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                    {...formik.getFieldProps('vat')}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
                <MaterialBatchManager
                    materialToEdit={materialToEdit}
                    onBatches={handleBatches}
                    onUseInventory={handleUseInventory}
                />
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box px={1}>
                    <FormControlLabel
                        control={
                            <Switch
                                id="inactive"
                                name="inactive"
                                checked={!formik.values.inactive}
                                onChange={(event) => formik.setFieldValue('inactive', !event.target.checked)}
                            />
                        }
                        label={!formik.values.inactive ? translate('common:ACTIVE') : translate('common:INACTIVE')}
                    />
                </Box>
                <div>
                    <Button sx={{ marginRight: '.5rem' }} onClick={onCloseDialog}>
                        {translate('common:CANCEL')}
                    </Button>
                    <Button variant="contained" disabled={!formik.isValid} type="submit">
                        {translate('lab-settings:SAVE_MATERIAL')}
                    </Button>
                </div>
            </DialogActions>
        </form>
    );
};

export default EditMaterialForm;
